import { useEffect, useRef, useState } from 'react'
import { Spinner } from '@/components/ui'
import { loadMercadoPagoSdk } from '@/lib/mercadopago'
import { logger } from '@/lib/logger'
import styles from './MercadoPagoStatusBrick.module.css'

interface MercadoPagoStatusBrickProps {
    /** Mercado Pago public key (APP_USR-… / TEST-…). */
    publicKey: string
    /** MP payment id returned by the backend on a `pending_challenge` response. */
    paymentId: string
    /**
     * Bank ACS challenge URL. The backend sends it as `three_ds.external_resource_url`;
     * it is forwarded to the Brick as `additionalInfo.externalResourceURL`.
     */
    externalResourceUrl: string
    /** Challenge request token (three_ds.creq). */
    creq: string
    /**
     * The buyer finished the bank challenge (Brick reached a terminal status).
     * The parent re-queries the order to learn the real outcome.
     */
    onChallengeResolved: () => void
    /** The Brick failed to load or render the challenge. */
    onError: (message: string) => void
}

// Stable container id for the Status Screen Brick. The Brick mounts itself
// inside the element with this id, so it must exist before `bricks.create`.
const BRICK_CONTAINER_ID = 'mercadopago-status-screen-brick-container'

// Generic fallback shown when the Brick fails. Includes a support contact per
// the payment-hardening spec; never surfaces raw MP error detail.
const BRICK_ERROR_MESSAGE =
    'No pudimos completar la verificación de seguridad. Intenta de nuevo o escríbenos a info@qolca.org.'

/**
 * Mercado Pago Status Screen Brick component.
 *
 * The Card Payment Brick does NOT render the 3DS challenge. When the backend
 * returns `pending_challenge`, this Brick is mounted with the returned
 * `paymentId` + `additionalInfo` (externalResourceURL + creq); it renders the
 * bank challenge inline and then shows the final status.
 *
 * Once the Brick is ready (the challenge has been presented and resolved), we
 * notify the parent via `onChallengeResolved` so it can re-query the order for
 * the authoritative outcome (the webhook is the source of truth) and route to
 * the existing success / masked-error UI.
 */
export function MercadoPagoStatusBrick({
    publicKey,
    paymentId,
    externalResourceUrl,
    creq,
    onChallengeResolved,
    onError,
}: MercadoPagoStatusBrickProps) {
    const [ready, setReady] = useState(false)
    const controllerRef = useRef<MercadoPagoBrickController | null>(null)

    // Stable refs for callbacks — avoids stale closures inside the Brick event
    // handlers while keeping the latest prop values reachable.
    const onChallengeResolvedRef = useRef(onChallengeResolved)
    const onErrorRef = useRef(onError)
    onChallengeResolvedRef.current = onChallengeResolved
    onErrorRef.current = onError

    useEffect(() => {
        const mounted = { current: true }

        const handleBrickError = (error: MercadoPagoBrickError) => {
            // Never surface MP's raw error.message; route through the sanitizing
            // logger and show the user a friendly generic message.
            logger.error('Mercado Pago Status Screen Brick error', error)
            if (mounted.current) onErrorRef.current(BRICK_ERROR_MESSAGE)
        }

        loadMercadoPagoSdk()
            .then(async () => {
                if (!mounted.current) return
                if (!window.MercadoPago) {
                    throw new Error('Mercado Pago no está disponible')
                }
                const mp = new window.MercadoPago(publicKey, { locale: 'es-PE' })
                const bricks = mp.bricks()
                const controller = await bricks.create(
                    'statusScreen',
                    BRICK_CONTAINER_ID,
                    {
                        initialization: {
                            paymentId,
                            additionalInfo: {
                                externalResourceURL: externalResourceUrl,
                                creq,
                            },
                        },
                        customization: {
                            visual: {
                                style: { theme: 'default' },
                                hideTransactionDate: true,
                            },
                        },
                        callbacks: {
                            onReady: () => {
                                if (!mounted.current) return
                                setReady(true)
                                // The challenge has been presented and the Brick
                                // has reached a status — let the parent re-query
                                // the order for the real outcome.
                                onChallengeResolvedRef.current()
                            },
                            onError: handleBrickError,
                        },
                    },
                )
                if (!mounted.current) {
                    try {
                        await controller?.unmount()
                    } catch {
                        /* race with unmount — safe to ignore */
                    }
                    return
                }
                controllerRef.current = controller
            })
            .catch((err) => {
                logger.error('Mercado Pago Status Screen Brick init failed', err)
                if (mounted.current) {
                    onErrorRef.current(
                        'No se pudo cargar la verificación de seguridad. Intenta de nuevo.',
                    )
                }
            })

        return () => {
            mounted.current = false
            const controller = controllerRef.current
            controllerRef.current = null
            if (controller) {
                try {
                    const maybePromise = controller.unmount()
                    if (
                        maybePromise &&
                        typeof (maybePromise as Promise<void>).catch === 'function'
                    ) {
                        (maybePromise as Promise<void>).catch(() => {
                            /* unmount can race with destroy — safe to ignore */
                        })
                    }
                } catch {
                    /* Brick may not be mounted — safe to ignore */
                }
            }
        }
    }, [publicKey, paymentId, externalResourceUrl, creq])

    return (
        <div className={styles.wrapper}>
            {!ready && (
                <div className={styles.loading} aria-live="polite" aria-busy="true">
                    <Spinner size="md" />
                    <p className={styles.loadingText}>
                        Completa la verificación de seguridad de tu banco...
                    </p>
                </div>
            )}

            {/* The Status Screen Brick mounts itself inside this container — it
                must exist before bricks.create is called, so it is rendered
                unconditionally. */}
            <div id={BRICK_CONTAINER_ID} className={styles.brickContainer} />
        </div>
    )
}
